import React, { useState, useMemo, useTransition } from 'react';
import { X, UserMinus, AlertCircle, CheckSquare, Square, Loader2, Search, CheckCircle } from 'lucide-react';
import { EmployeeWithAssignments } from '../../types/employee';
import { apiService } from '../../services/api';

interface UnassignModalProps {
  employee: EmployeeWithAssignments;
  onClose: () => void;
  onSuccess: () => void;
}

export const UnassignModal: React.FC<UnassignModalProps> = ({
  employee,
  onClose,
  onSuccess
}) => {
  const [selectedAccounts, setSelectedAccounts] = useState<Set<number>>(new Set());
  const [unassignAll, setUnassignAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isPending, startTransition] = useTransition();
  const [searchTerm, setSearchTerm] = useState('');

  const activeAssignments = useMemo(
    () => employee.user_accounts.filter(ua => ua.is_active),
    [employee.user_accounts]
  );

  const filteredAssignments = useMemo(() => {
    if (!searchTerm.trim()) {
      return activeAssignments;
    }

    const term = searchTerm.toLowerCase();
    return activeAssignments.filter(assignment =>
      assignment.account.account_name.toLowerCase().includes(term) ||
      assignment.account.client?.client_name?.toLowerCase().includes(term)
    );
  }, [activeAssignments, searchTerm]);

  const toggleAccount = (userAccountId: number) => {
    startTransition(() => {
      const newSelection = new Set(selectedAccounts);
      if (newSelection.has(userAccountId)) {
        newSelection.delete(userAccountId);
      } else {
        newSelection.add(userAccountId);
      }
      setSelectedAccounts(newSelection);

      // If user is manually selecting/deselecting, turn off "unassign all"
      if (unassignAll) {
        setUnassignAll(false);
      }
    });
  };

  const toggleSelectAll = () => {
    startTransition(() => {
      if (unassignAll) {
        setUnassignAll(false);
        setSelectedAccounts(new Set());
      } else {
        setUnassignAll(true);
        setSelectedAccounts(new Set(activeAssignments.map(ua => ua.id)));
      }
    });
  };

  const handleSubmit = async () => {
    if (selectedAccounts.size === 0 && !unassignAll) {
      setError('Please select at least one account to unassign');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      // Get all assignments to remove
      const assignmentsToRemove = Array.from(selectedAccounts);

      // Use bulk API endpoint - ONE request for all unassignments
      const response = await apiService.bulkRemoveEmployeeFromAccounts(assignmentsToRemove, false);

      // Show success message
      const count = response.data?.summary?.successful_count || assignmentsToRemove.length;
      setSuccessMessage(`Successfully unassigned from ${count} account${count !== 1 ? 's' : ''}`);
      setSuccess(true);

      // Refresh parent data
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to unassign employee from accounts');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Unassign from Accounts</h3>
            <p className="text-sm text-gray-600 mt-1">{employee.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={submitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Info */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <UserMinus className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-900">
                  Select accounts to unassign this employee from
                </p>
                <p className="text-xs text-orange-700 mt-1">
                  This will remove the employee's commission assignments from the selected accounts
                </p>
              </div>
            </div>
          </div>

          {/* Unassign All Option */}
          {activeAssignments.length > 0 && (
            <div className="space-y-3">
              <div
                onClick={toggleSelectAll}
                className={`flex items-start space-x-3 p-3 border-2 border-orange-200 rounded-lg transition-colors ${
                  isPending ? 'cursor-wait opacity-75' : 'cursor-pointer hover:bg-orange-50'
                }`}
              >
                <div className="mt-0.5">
                  {isPending ? (
                    <Loader2 className="h-5 w-5 text-orange-600 animate-spin" />
                  ) : unassignAll ? (
                    <CheckSquare className="h-5 w-5 text-orange-600" />
                  ) : (
                    <Square className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    Unassign from all {activeAssignments.length} active account{activeAssignments.length !== 1 ? 's' : ''}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {isPending
                      ? 'Processing selection...'
                      : 'Remove this employee from all active account assignments'
                    }
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Account List */}
          {activeAssignments.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">
                  {unassignAll ? 'Selected accounts (click to deselect):' : 'Or select specific accounts:'}
                </h4>
                <span className="text-xs text-gray-500">
                  {filteredAssignments.length} of {activeAssignments.length} accounts
                </span>
              </div>

              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by account or client name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={isPending || submitting}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className={`space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 ${
                isPending ? 'opacity-50 pointer-events-none' : ''
              }`}>
                {filteredAssignments.length > 0 ? (
                  filteredAssignments.map((assignment) => (
                    <div
                      key={assignment.id}
                      onClick={() => !isPending && toggleAccount(assignment.id)}
                      className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                        selectedAccounts.has(assignment.id)
                          ? 'bg-orange-50 border border-orange-200'
                          : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                      } ${isPending ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                    >
                      <div className="mt-0.5">
                        {selectedAccounts.has(assignment.id) ? (
                          <CheckSquare className="h-5 w-5 text-orange-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {assignment.account.account_name}
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-gray-500">
                            {assignment.account.client?.client_name}
                          </span>
                          <span className="text-xs font-medium text-orange-600">
                            {parseFloat(assignment.commission_percentage.toString()).toFixed(1)}% commission
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No accounts found matching "{searchTerm}"</p>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="text-xs text-orange-600 hover:text-orange-700 mt-2"
                    >
                      Clear search
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No active account assignments</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">{successMessage}</p>
                  <p className="text-xs text-green-700 mt-1">You can close this modal or continue with other operations</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {success ? (
              <span className="text-green-600 font-medium">Operation completed successfully</span>
            ) : unassignAll ? (
              `Unassigning from ${activeAssignments.length} account${activeAssignments.length !== 1 ? 's' : ''}`
            ) : selectedAccounts.size > 0 ? (
              `${selectedAccounts.size} account${selectedAccounts.size !== 1 ? 's' : ''} selected`
            ) : (
              'No accounts selected'
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={submitting}
            >
              {success ? 'Close' : 'Cancel'}
            </button>
            {!success && (
              <button
                onClick={handleSubmit}
                disabled={submitting || (selectedAccounts.size === 0 && !unassignAll)}
                className="btn bg-orange-600 hover:bg-orange-700 text-white flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Unassigning...</span>
                  </>
                ) : (
                  <>
                    <UserMinus className="h-4 w-4" />
                    <span>Unassign</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
